import React, { useState, useEffect } from 'react';
import {
    Settings,
    Save,
    DollarSign,
    CreditCard,
    Shield,
    AlertCircle,
    RefreshCw,
    CheckCircle
} from 'lucide-react';
import { 
    useGetMarketplaceSettingsQuery,
    useUpdateMarketplaceSettingsMutation,
    UpdateMarketplaceSettingsRequest
} from '../../store/slices/apiSlice';
import { Button } from '../../components/ui/Button';
import toast from 'react-hot-toast';
import type { MarketplaceSettings as MarketplaceSettingsType } from '../../store/slices/apiSlice';

const MarketplaceSettings: React.FC = () => {
    const { data: settingsData, isLoading, error, refetch } = useGetMarketplaceSettingsQuery();
    const [updateSettings, { isLoading: isUpdating }] = useUpdateMarketplaceSettingsMutation();

    const settings: MarketplaceSettingsType | undefined = settingsData?.data;

    const [formData, setFormData] = useState<UpdateMarketplaceSettingsRequest>({
        min_transaction_amount: 0,
        max_transaction_amount: 0,
        large_transaction_flag_amount: 0,
        post_kyc_transaction_limit: 0,
        wallet_without_kyc_limit: 0,
        wallet_daily_cap: 0,
        allow_wallet_payments: false,
        allow_bank_transfer: false,
        allow_online_gateway: false,
    });

    const [errors, setErrors] = useState<Partial<Record<keyof UpdateMarketplaceSettingsRequest, string>>>({});

    // Populate form with settings data when loaded
    useEffect(() => {
        if (settings) {
            setFormData({
                min_transaction_amount: parseFloat(settings.min_transaction_amount) || 0,
                max_transaction_amount: parseFloat(settings.max_transaction_amount) || 0,
                large_transaction_flag_amount: parseFloat(settings.large_transaction_flag_amount) || 0,
                post_kyc_transaction_limit: parseFloat(settings.post_kyc_transaction_limit) || 0,
                wallet_without_kyc_limit: parseFloat(settings.wallet_without_kyc_limit) || 0,
                wallet_daily_cap: parseFloat(settings.wallet_daily_cap) || 0,
                allow_wallet_payments: settings.allow_wallet_payments,
                allow_bank_transfer: settings.allow_bank_transfer,
                allow_online_gateway: settings.allow_online_gateway,
            });
        }
    }, [settings]);

    const handleInputChange = (field: keyof UpdateMarketplaceSettingsRequest, value: any) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));

        if (errors[field]) {
            setErrors(prev => ({
                ...prev,
                [field]: undefined
            }));
        }
    };

    const validateForm = (): boolean => {
        const newErrors: Partial<Record<keyof UpdateMarketplaceSettingsRequest, string>> = {};

        if (formData.min_transaction_amount < 0) {
            newErrors.min_transaction_amount = 'Minimum transaction amount cannot be negative';
        }

        if (formData.max_transaction_amount < 0) {
            newErrors.max_transaction_amount = 'Maximum transaction amount cannot be negative';
        }

        if (formData.min_transaction_amount > formData.max_transaction_amount) {
            newErrors.min_transaction_amount = 'Minimum amount cannot be greater than maximum amount';
        }

        if (formData.large_transaction_flag_amount < 0) {
            newErrors.large_transaction_flag_amount = 'Large transaction flag amount cannot be negative';
        }

        if (formData.post_kyc_transaction_limit < 0) {
            newErrors.post_kyc_transaction_limit = 'Post KYC transaction limit cannot be negative';
        }

        if (formData.wallet_without_kyc_limit < 0) {
            newErrors.wallet_without_kyc_limit = 'Wallet without KYC limit cannot be negative';
        }

        if (formData.wallet_daily_cap < 0) {
            newErrors.wallet_daily_cap = 'Wallet daily cap cannot be negative';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!validateForm()) {
            return;
        }

        try {
            await updateSettings(formData).unwrap();
            toast.success('Marketplace settings updated successfully!');
            refetch();
        } catch (error: any) {
            console.error('Failed to update settings:', error);
            toast.error(error?.data?.message || 'Failed to update settings');
        }
    };

    if (isLoading) {
        return (
            <div className="p-6">
                <div className="flex items-center justify-center h-64">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
                </div>
            </div>
        );
    }

    if (error || !settings) {
        return (
            <div className="p-6">
                <div className="bg-red-50 border border-red-200 rounded-lg p-6">
                    <p className="text-red-800">Error loading settings. Please try again.</p>
                    <button
                        onClick={() => refetch()}
                        className="mt-4 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
                    >
                        Retry
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="p-6">
            <form onSubmit={handleSubmit} className="space-y-6">
                {/* Transaction Limits */}
                <div className="bg-white rounded-lg shadow">
                    <div className="px-6 py-4 border-b border-gray-200">
                        <h2 className="text-lg font-semibold text-gray-900 flex items-center">
                            <DollarSign className="w-5 h-5 mr-2" />
                            Transaction Limits
                        </h2>
                    </div>
                    <div className="p-6 space-y-6">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Minimum Transaction Amount *
                                </label>
                                <div className="relative">
                                    <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                                    <input
                                        type="number"
                                        value={formData.min_transaction_amount || 0}
                                        onChange={(e) => handleInputChange('min_transaction_amount', parseFloat(e.target.value) || 0)}
                                        className={`w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                                            errors.min_transaction_amount ? 'border-red-500' : 'border-gray-300'
                                        }`}
                                        placeholder="0.00"
                                        min="0"
                                        step="0.01"
                                    />
                                </div>
                                {errors.min_transaction_amount && (
                                    <p className="mt-1 text-sm text-red-600">{errors.min_transaction_amount}</p>
                                )}
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Maximum Transaction Amount *
                                </label>
                                <div className="relative">
                                    <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                                    <input
                                        type="number"
                                        value={formData.max_transaction_amount || 0}
                                        onChange={(e) => handleInputChange('max_transaction_amount', parseFloat(e.target.value) || 0)}
                                        className={`w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                                            errors.max_transaction_amount ? 'border-red-500' : 'border-gray-300'
                                        }`}
                                        placeholder="0.00"
                                        min="0"
                                        step="0.01"
                                    />
                                </div>
                                {errors.max_transaction_amount && (
                                    <p className="mt-1 text-sm text-red-600">{errors.max_transaction_amount}</p>
                                )}
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Large Transaction Flag Amount *
                                </label>
                                <div className="relative">
                                    <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                                    <input
                                        type="number"
                                        value={formData.large_transaction_flag_amount || 0}
                                        onChange={(e) => handleInputChange('large_transaction_flag_amount', parseFloat(e.target.value) || 0)}
                                        className={`w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                                            errors.large_transaction_flag_amount ? 'border-red-500' : 'border-gray-300'
                                        }`}
                                        placeholder="0.00"
                                        min="0"
                                        step="0.01"
                                    />
                                </div>
                                {errors.large_transaction_flag_amount && (
                                    <p className="mt-1 text-sm text-red-600">{errors.large_transaction_flag_amount}</p>
                                )}
                                <p className="mt-1 text-xs text-gray-500">Transactions above this amount will be flagged as large transactions</p>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Post KYC Transaction Limit *
                                </label>
                                <div className="relative">
                                    <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                                    <input
                                        type="number"
                                        value={formData.post_kyc_transaction_limit || 0}
                                        onChange={(e) => handleInputChange('post_kyc_transaction_limit', parseFloat(e.target.value) || 0)}
                                        className={`w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                                            errors.post_kyc_transaction_limit ? 'border-red-500' : 'border-gray-300'
                                        }`}
                                        placeholder="0.00"
                                        min="0"
                                        step="0.01"
                                    />
                                </div>
                                {errors.post_kyc_transaction_limit && (
                                    <p className="mt-1 text-sm text-red-600">{errors.post_kyc_transaction_limit}</p>
                                )}
                                <p className="mt-1 text-xs text-gray-500">Maximum transaction amount after KYC approval</p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Wallet Limits */}
                <div className="bg-white rounded-lg shadow">
                    <div className="px-6 py-4 border-b border-gray-200">
                        <h2 className="text-lg font-semibold text-gray-900 flex items-center">
                            <CreditCard className="w-5 h-5 mr-2" />
                            Wallet Limits
                        </h2>
                    </div>
                    <div className="p-6 space-y-6">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Wallet Without KYC Limit *
                                </label>
                                <div className="relative">
                                    <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                                    <input
                                        type="number"
                                        value={formData.wallet_without_kyc_limit || 0}
                                        onChange={(e) => handleInputChange('wallet_without_kyc_limit', parseFloat(e.target.value) || 0)}
                                        className={`w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                                            errors.wallet_without_kyc_limit ? 'border-red-500' : 'border-gray-300'
                                        }`}
                                        placeholder="0.00"
                                        min="0"
                                        step="0.01"
                                    />
                                </div>
                                {errors.wallet_without_kyc_limit && (
                                    <p className="mt-1 text-sm text-red-600">{errors.wallet_without_kyc_limit}</p>
                                )}
                                <p className="mt-1 text-xs text-gray-500">Maximum wallet transaction amount without KYC</p>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Wallet Daily Cap *
                                </label>
                                <div className="relative">
                                    <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                                    <input
                                        type="number"
                                        value={formData.wallet_daily_cap || 0}
                                        onChange={(e) => handleInputChange('wallet_daily_cap', parseFloat(e.target.value) || 0)}
                                        className={`w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                                            errors.wallet_daily_cap ? 'border-red-500' : 'border-gray-300'
                                        }`}
                                        placeholder="0.00"
                                        min="0"
                                        step="0.01"
                                    />
                                </div>
                                {errors.wallet_daily_cap && (
                                    <p className="mt-1 text-sm text-red-600">{errors.wallet_daily_cap}</p>
                                )}
                                <p className="mt-1 text-xs text-gray-500">Maximum daily wallet transaction limit</p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Payment Methods */}
                <div className="bg-white rounded-lg shadow">
                    <div className="px-6 py-4 border-b border-gray-200">
                        <h2 className="text-lg font-semibold text-gray-900 flex items-center">
                            <CreditCard className="w-5 h-5 mr-2" />
                            Payment Methods
                        </h2>
                    </div>
                    <div className="p-6 space-y-4">
                        <div className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
                            <div>
                                <label className="text-sm font-medium text-gray-900">Allow Wallet Payments</label>
                                <p className="text-xs text-gray-500 mt-1">Enable wallet as a payment method</p>
                            </div>
                            <label className="relative inline-flex items-center cursor-pointer">
                                <input
                                    type="checkbox"
                                    checked={formData.allow_wallet_payments || false}
                                    onChange={(e) => handleInputChange('allow_wallet_payments', e.target.checked)}
                                    className="sr-only peer"
                                />
                                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                            </label>
                        </div>

                        <div className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
                            <div>
                                <label className="text-sm font-medium text-gray-900">Allow Bank Transfer</label>
                                <p className="text-xs text-gray-500 mt-1">Enable bank transfer as a payment method</p>
                            </div>
                            <label className="relative inline-flex items-center cursor-pointer">
                                <input
                                    type="checkbox"
                                    checked={formData.allow_bank_transfer || false}
                                    onChange={(e) => handleInputChange('allow_bank_transfer', e.target.checked)}
                                    className="sr-only peer"
                                />
                                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                            </label>
                        </div>

                        <div className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
                            <div>
                                <label className="text-sm font-medium text-gray-900">Allow Online Gateway</label>
                                <p className="text-xs text-gray-500 mt-1">Enable online payment gateway</p>
                            </div>
                            <label className="relative inline-flex items-center cursor-pointer">
                                <input
                                    type="checkbox"
                                    checked={formData.allow_online_gateway || false}
                                    onChange={(e) => handleInputChange('allow_online_gateway', e.target.checked)}
                                    className="sr-only peer"
                                />
                                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                            </label>
                        </div>
                    </div>
                </div>

                {/* Actions */}
                <div className="flex justify-end gap-4 pb-8">
                    <Button
                        type="button"
                        variant="outline"
                        onClick={() => refetch()}
                        disabled={isLoading || isUpdating}
                        className="text-gray-700 border-gray-300 hover:bg-gray-50"
                    >
                        <RefreshCw className="w-4 h-4 mr-2" />
                        Refresh
                    </Button>
                    <Button
                        type="submit"
                        variant="primary"
                        disabled={isUpdating}
                        isLoading={isUpdating}
                    >
                        <Save className="w-4 h-4 mr-2" />
                        Save Settings
                    </Button>
                </div>
            </form>
        </div>
    );
};

export default MarketplaceSettings;

